import { useEffect, useState, useRef, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDateTime as fmtDT, formatDatePrint } from '../utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash, Notebook, MagnifyingGlass, Pencil, CaretDown, Check, X, EnvelopeSimple, ChatCircleDots, Printer, FileDoc } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';
import { printHTML, downloadWord } from '../utils/printHelper';
import { confirmDialog } from '@/components/ConfirmDialog';

const FALLBACK_VRSTA = [
  { value: 'pdv', label: 'PDV' },
  { value: 'prijava_radnika', label: 'Prijava radnika' },
  { value: 'odjava_radnika', label: 'Odjava radnika' },
  { value: 'ostalo', label: 'Ostalo' },
];

const STATUS_MAP = {
  u_toku: { label: 'U toku', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  zavrseno: { label: 'Zavrseno', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  otkazano: { label: 'Otkazano', bg: 'bg-red-50 text-red-700 border-red-200' },
};

// Searchable dropdown component
function SearchableSelect({ options, value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.value === value);
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button type="button" onClick={() => { setOpen(!open); setSearch(''); }}
        className="flex items-center justify-between w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500">
        <span className={selected ? 'text-slate-900 truncate' : 'text-slate-500 truncate'}>{selected ? selected.label : placeholder}</span>
        <CaretDown size={14} className="text-slate-400 shrink-0 ml-1" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input ref={inputRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Pretrazi..." className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">Nema rezultata</div>
            ) : filtered.map(o => (
              <button key={o.value} type="button"
                onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 ${o.value === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}>
                {o.value === value && <Check size={14} className="text-blue-600 shrink-0" />}
                <span className="truncate">{o.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DnevnikPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canFilterUsers = (user.role === 'admin' || user.role === 'super_admin' || user.role === 'supervizor');

  const [form, setForm] = useState({ client_id: '', vrsta_posla: 'ostalo', opis_posla: '', status: 'zavrseno' });
  const [editingId, setEditingId] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [filterVrsta, setFilterVrsta] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDatumOd, setFilterDatumOd] = useState('');
  const [filterDatumDo, setFilterDatumDo] = useState('');

  // Dynamic config
  const [vrstaPoslaList, setVrstaPoslaList] = useState(FALLBACK_VRSTA);
  const vrstaMap = Object.fromEntries(vrstaPoslaList.map(v => [v.value, v.label]));
  // Notify
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEntry, setNotifyEntry] = useState(null);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  const [notifyFile, setNotifyFile] = useState(null);
  const [notifyChannel, setNotifyChannel] = useState('email');
  const [viberSending, setViberSending] = useState(null);
  // Pagination
  const [page, setPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const PAGE_SIZE = 50;
  // Autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const suggestionsRef = useRef(null);
  const opisRef = useRef(null);

  useEffect(() => { fetchEntries(1); fetchClients(); fetchUsers(); fetchConfig(); }, []);

  useEffect(() => { setPage(1); fetchEntries(1); }, [filterClient, filterUser, filterDatumOd, filterDatumDo]);

  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          opisRef.current && !opisRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchEntries = async (p = page) => {
    try {
      const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
      if (filterClient !== 'all') params.set('client_id', filterClient);
      if (filterUser !== 'all') params.set('korisnik_id', filterUser);
      if (filterDatumOd) params.set('datum_od', filterDatumOd);
      if (filterDatumDo) params.set('datum_do', filterDatumDo);
      const r = await api.get(`/dnevnik?${params.toString()}`);
      setEntries(r.data.entries);
      setTotalEntries(r.data.total);
    }
    catch { toast.error('Greska pri ucitavanju'); } finally { setLoading(false); }
  };
  const fetchClients = async () => { try { const r = await api.get('/clients'); setClients(r.data); } catch {} };
  const fetchUsers = async () => { try { const r = await api.get('/users'); setUsers(r.data); } catch {} };
  const fetchConfig = async () => {
    try {
      const r = await api.get('/settings/config-lists');
      const vp = r.data.vrste_posla || [];
      if (vp.length > 0) {
        setVrstaPoslaList(vp.map(v => ({ value: v.toLowerCase().replace(/\s+/g, '_'), label: v })));
      }
    } catch {}
  };

  // Clients sorted alphabetically
  const sortedClients = useMemo(() =>
    [...clients].sort((a, b) => (a.skraceni_naziv || a.naziv_firme || '').localeCompare(b.skraceni_naziv || b.naziv_firme || '')),
    [clients]
  );

  const clientOptions = useMemo(() => [
    { value: '', label: '-- Bez klijenta --' },
    ...sortedClients.map(c => ({ value: c.id, label: `${c.skraceni_naziv || c.naziv_firme || ''}${c.pib ? ` (PIB: ${c.pib})` : ''}` }))
  ], [sortedClients]);

  const filterClientOptions = useMemo(() => [
    { value: 'all', label: 'Svi klijenti' },
    ...sortedClients.map(c => ({ value: c.id, label: `${c.skraceni_naziv || c.naziv_firme || ''}${c.pib ? ` (PIB: ${c.pib})` : ''}` }))
  ], [sortedClients]);

  const filterVrstaOptions = useMemo(() => [
    { value: 'all', label: 'Sve vrste posla' },
    ...vrstaPoslaList
  ], [vrstaPoslaList]);

  const getClientName = (id) => {
    const c = clients.find(c => c.id === id);
    return c ? (c.skraceni_naziv || c.naziv_firme) : '';
  };

  // Unique previous descriptions for autocomplete
  const uniqueDescriptions = useMemo(() => {
    const seen = new Set();
    return entries.map(e => e.opis_posla).filter(Boolean).filter(d => {
      const k = d.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [entries]);

  const handleOpisChange = (value) => {
    setForm(prev => ({ ...prev, opis_posla: value }));
    if (value.length >= 2) {
      const matching = uniqueDescriptions.filter(d => d.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(matching.slice(0, 8));
      setShowSuggestions(matching.length > 0);
      setActiveSuggestion(-1);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (s) => {
    setForm(prev => ({ ...prev, opis_posla: s }));
    setShowSuggestions(false);
    opisRef.current?.focus();
  };

  const handleOpisKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeSuggestion >= suggestions.length - 1) {
        // At last suggestion - close dropdown
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      } else {
        setActiveSuggestion(p => p + 1);
      }
    }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveSuggestion(p => Math.max(p - 1, -1)); }
    else if (e.key === 'Enter' && activeSuggestion >= 0) { e.preventDefault(); handleSuggestionSelect(suggestions[activeSuggestion]); }
    else if (e.key === 'Escape' || e.key === 'Tab') setShowSuggestions(false);
  };

  const filtered = entries.filter(e => {
    if (filterVrsta !== 'all' && (e.vrsta_posla || 'ostalo') !== filterVrsta) return false;
    if (filterStatus !== 'all' && (e.status || 'zavrseno') !== filterStatus) return false;
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      if (!e.opis_posla?.toLowerCase().includes(s) && !getClientName(e.client_id).toLowerCase().includes(s) && !e.korisnik_ime?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(totalEntries / PAGE_SIZE);
  const goPage = (p) => { setPage(p); fetchEntries(p); window.scrollTo({ top: 300, behavior: 'smooth' }); };

  const handleSubmit = async () => {
    if (!form.opis_posla.trim()) { toast.error('Unesite opis posla'); return; }
    try {
      if (editingId) { await api.put(`/dnevnik/${editingId}`, form); toast.success('Azurirano'); setEditingId(null); }
      else { await api.post('/dnevnik', form); toast.success('Zapisano'); }
      setForm({ client_id: '', vrsta_posla: 'ostalo', opis_posla: '', status: 'zavrseno' });
      setShowSuggestions(false);
      fetchEntries(page);
    } catch (error) { toast.error(error.response?.data?.detail || 'Greska'); }
  };

  const handleEdit = (e) => {
    setEditingId(e.id);
    setForm({ client_id: e.client_id || '', vrsta_posla: e.vrsta_posla || 'ostalo', opis_posla: e.opis_posla || '', status: e.status || 'zavrseno' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({ client_id: '', vrsta_posla: 'ostalo', opis_posla: '', status: 'zavrseno' });
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati zapis?'))) return;
    try { await api.delete(`/dnevnik/${id}`); toast.success('Obrisano'); fetchEntries(page); }
    catch { toast.error('Greska'); }
  };

  const buildDnevnikHtml = () => {
    const clientName = filterClient !== 'all' ? getClientName(filterClient) : 'Svi klijenti';
    const today = new Date();
    const dateStr = `${today.getDate().toString().padStart(2,'0')}.${(today.getMonth()+1).toString().padStart(2,'0')}.${today.getFullYear()}`;
    const periodStr = filterDatumOd || filterDatumDo ? `${formatDatePrint(filterDatumOd) || ''} - ${formatDatePrint(filterDatumDo) || ''}` : 'Svi datumi';

    const rows = entries.map((e, i) => `
      <tr>
        <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;font-size:10pt">${i+1}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;font-size:10pt">${getClientName(e.client_id)}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;font-size:10pt">${e.opis_posla || ''}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;font-size:10pt;text-align:center">${e.vrsta_posla || ''}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;font-size:10pt;text-align:center">${e.korisnik_ime || ''}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;font-size:10pt;text-align:center;white-space:nowrap">${fmtDT(e.datum_vrijeme || e.created_at)}</td>
      </tr>`).join('');

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Dnevnik rada - ${clientName}</title>
<style>
@page{size:A4 landscape;margin:12mm 15mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:11pt;color:#000}
h1{text-align:center;font-size:14pt;margin-bottom:4px}
.info{text-align:center;font-size:10pt;color:#333;margin-bottom:10px}
table{width:100%;border-collapse:collapse;margin-top:6px}
th{border:1px solid #000;padding:5px 6px;background:#f0f0f0;font-size:9pt;text-align:center}
.footer{margin-top:15px;font-size:9pt;color:#666;text-align:right}
@media print{body{margin:0}th{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<h1>DNEVNIK RADA</h1>
<div class="info">Klijent: ${clientName} | Period: ${periodStr} | Ukupno: ${totalEntries} zapisa (str. ${page}) | Datum štampe: ${dateStr}</div>
<table>
<thead><tr>
<th style="width:30px">R.br</th>
<th>Klijent</th>
<th>Opis posla</th>
<th style="width:100px">Vrsta</th>
<th style="width:100px">Korisnik</th>
<th style="width:120px">Datum</th>
</tr></thead>
<tbody>${rows}</tbody>
</table>
<div class="footer">CRM FINANCE - FINANCE GROUP DOO | ${dateStr}</div>
</body></html>`;
  };

  const handlePrintDnevnik = () => printHTML(buildDnevnikHtml());
  const handleWordDnevnik = () => {
    const clientName = filterClient !== 'all' ? getClientName(filterClient) : 'Svi';
    downloadWord(buildDnevnikHtml(), `Dnevnik_rada_${clientName.replace(/\s+/g,'_')}.doc`);
  };


  const handleStatusChange = async (entryId, newStatus) => {
    try {
      await api.patch(`/dnevnik/${entryId}/status?status=${newStatus}`);
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, status: newStatus } : e));
    } catch { toast.error('Greska pri promjeni statusa'); }
  };

  const handleNotifyOpen = (entry, channel = 'email') => {
    if (channel === 'email' && entry.notified_at) { toast.info('Email je već poslan za ovaj zapis'); return; }
    if (channel === 'viber' && entry.viber_notified_at) { toast.info('Viber poruka je već poslata za ovaj zapis'); return; }
    if (!entry.client_id) { toast.error('Zapis nema klijenta'); return; }
    const c = clients.find(cl => cl.id === entry.client_id);
    if (channel === 'email' && !c?.email) { toast.error('Klijent nema email adresu'); return; }
    if (channel === 'viber' && !c?.telefon) { toast.error('Klijent nema telefonski broj'); return; }
    setNotifyEntry(entry);
    setNotifyChannel(channel);
    setNotifyMsg('');
    setNotifyFile(null);
    setNotifyOpen(true);
  };

  const handleNotifySend = async () => {
    if (!notifyEntry) return;
    setNotifySending(true);
    try {
      if (notifyChannel === 'viber') {
        const formData = new FormData();
        formData.append('poruka', notifyMsg);
        await api.post(`/dnevnik/${notifyEntry.id}/notify-viber`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Viber poruka poslata klijentu!');
        setEntries(prev => prev.map(e => e.id === notifyEntry.id ? { ...e, viber_notified_at: new Date().toISOString() } : e));
      } else {
        const formData = new FormData();
        formData.append('poruka', notifyMsg);
        if (notifyFile) formData.append('file', notifyFile);
        await api.post(`/dnevnik/${notifyEntry.id}/notify`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Email poslan klijentu!');
        setEntries(prev => prev.map(e => e.id === notifyEntry.id ? { ...e, notified_at: new Date().toISOString() } : e));
      }
      setNotifyOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri slanju');
    } finally { setNotifySending(false); }
  };

  const formatDateTime = (dt) => fmtDT(dt);

  return (
    <div className="space-y-6" data-testid="dnevnik-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Dnevnik rada</h3>
          <p className="text-sm text-slate-600 mt-1">Evidencija radnih aktivnosti</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handlePrintDnevnik} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" disabled={filtered.length === 0}>
            <Printer size={16} className="mr-2" /> Štampaj
          </Button>
          <Button onClick={handleWordDnevnik} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" disabled={filtered.length === 0}>
            <FileDoc size={16} className="mr-2" /> Word
          </Button>
        </div>
      </div>

      {/* Quick Add / Edit */}
      <Card className={`p-5 ${editingId ? 'border-amber-300 bg-amber-50/30' : 'border-blue-200 bg-blue-50/30'}`} data-testid="dnevnik-quick-add">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-800">{editingId ? 'Izmjena zapisa' : 'Novi zapis'}</h4>
          {editingId && <button onClick={handleCancelEdit} className="text-xs text-slate-500 hover:text-slate-700 underline">Otkazi izmjenu</button>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Vrsta posla</Label>
            <SearchableSelect options={vrstaPoslaList} value={form.vrsta_posla} onChange={v => setForm({ ...form, vrsta_posla: v })} placeholder="Izaberite" testId="dnevnik-vrsta-select" />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent</Label>
            <SearchableSelect options={clientOptions} value={form.client_id} onChange={v => setForm({ ...form, client_id: v })} placeholder="Izaberite klijenta" testId="dnevnik-client-select" />
          </div>
          <div className="md:col-span-5 space-y-1 relative">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Opis posla *</Label>
            <input ref={opisRef} type="text" value={form.opis_posla}
              onChange={e => handleOpisChange(e.target.value)}
              onKeyDown={handleOpisKeyDown}
              onFocus={() => { if (form.opis_posla.length >= 2) handleOpisChange(form.opis_posla); }}
              placeholder="Sta ste radili..."
              className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="dnevnik-opis-input" autoComplete="off" />
            {showSuggestions && suggestions.length > 0 && (
              <div ref={suggestionsRef} className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="dnevnik-suggestions">
                {suggestions.map((s, i) => (
                  <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); handleSuggestionSelect(s); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0 ${i === activeSuggestion ? 'bg-blue-50 text-blue-700' : 'text-slate-700'}`}>{s}</button>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-1 space-y-1">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Status</Label>
            <SearchableSelect options={Object.entries(STATUS_MAP).map(([k, v]) => ({ value: k, label: v.label }))} value={form.status} onChange={v => setForm({ ...form, status: v })} placeholder="Status" testId="dnevnik-status-select" />
          </div>
          <div className="md:col-span-2">
          <Button onClick={handleSubmit} className={`w-full ${editingId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-md px-4 py-2 font-medium h-10`} data-testid="dnevnik-submit-button">
            {editingId ? <><Pencil size={18} weight="bold" className="mr-2" /> Azuriraj</> : <><Plus size={18} weight="bold" className="mr-2" /> Zapisi</>}
          </Button>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4" data-testid="dnevnik-filters">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Pretraga..." className="pl-9 bg-white border-slate-300 text-sm" />
          </div>
          <SearchableSelect options={filterVrstaOptions} value={filterVrsta} onChange={setFilterVrsta} placeholder="Vrsta posla" testId="dnevnik-filter-vrsta" />
          <SearchableSelect options={filterClientOptions} value={filterClient} onChange={setFilterClient} placeholder="Klijent" testId="dnevnik-filter-client" />
          <SearchableSelect
            options={[{ value: 'all', label: 'Svi statusi' }, ...Object.entries(STATUS_MAP).map(([k, v]) => ({ value: k, label: v.label }))]}
            value={filterStatus} onChange={setFilterStatus} placeholder="Status" testId="dnevnik-filter-status" />
          {canFilterUsers && (
            <SearchableSelect
              options={[{ value: 'all', label: 'Svi korisnici' }, ...users.map(u => ({ value: u.id, label: u.name }))]}
              value={filterUser} onChange={setFilterUser} placeholder="Korisnik" testId="dnevnik-filter-user" />
          )}
          <DateInput value={filterDatumOd} onChange={e => setFilterDatumOd(e.target.value)} className="border-slate-300 text-sm" />
          <DateInput value={filterDatumDo} onChange={e => setFilterDatumDo(e.target.value)} className="border-slate-300 text-sm" />
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><p className="text-slate-500">Ucitavanje...</p></div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center"><Notebook size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" /><p className="text-slate-500">Nema zapisa</p></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600" data-testid="dnevnik-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Datum i vrijeme</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Radnik</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Opis dogadjaja</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Vrsta posla</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.id} className={`border-b border-slate-200 hover:bg-slate-50 ${editingId === e.id ? 'bg-amber-50' : ''}`} data-testid={`dnevnik-row-${e.id}`}>
                    <td className="px-4 py-3"><p className="text-xs font-medium text-slate-900 whitespace-nowrap">{formatDateTime(e.datum_vrijeme)}</p></td>
                    <td className="px-4 py-3 font-medium text-sm whitespace-nowrap">{e.korisnik_ime || '-'}</td>
                    <td className="px-4 py-3 text-sm">{getClientName(e.client_id) || e.client_name || '-'}</td>
                    <td className="px-4 py-3 text-sm max-w-xs truncate" title={e.opis_posla}>{e.opis_posla}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 whitespace-nowrap">
                        {vrstaMap[e.vrsta_posla] || e.vrsta_posla || 'Ostalo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select value={e.status || 'zavrseno'} onChange={ev => handleStatusChange(e.id, ev.target.value)}
                        className={`px-2 py-1 rounded text-xs font-medium border cursor-pointer outline-none ${STATUS_MAP[e.status || 'zavrseno']?.bg || 'bg-slate-50 text-slate-600 border-slate-200'}`}
                        data-testid={`dnevnik-status-${e.id}`}>
                        {Object.entries(STATUS_MAP).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 items-center">
                        {e.notified_at ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200 whitespace-nowrap" title={`Email poslan: ${fmtDT(e.notified_at)}`}>
                            <EnvelopeSimple size={12} weight="bold" className="inline mr-1" />Poslan
                          </span>
                        ) : (
                          <button onClick={() => handleNotifyOpen(e, 'email')} className="p-2 hover:bg-green-100 rounded transition-colors" title="Pošalji email" data-testid={`dnevnik-notify-${e.id}`}>
                            <EnvelopeSimple size={16} className="text-slate-400" />
                          </button>
                        )}
                        {e.viber_notified_at ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 whitespace-nowrap" title={`Viber poslan: ${fmtDT(e.viber_notified_at)}`}>
                            <ChatCircleDots size={12} weight="bold" className="inline mr-1" />Viber
                          </span>
                        ) : (
                          <button onClick={() => handleNotifyOpen(e, 'viber')} className="p-2 hover:bg-purple-100 rounded transition-colors" title="Pošalji Viber" data-testid={`dnevnik-viber-${e.id}`}>
                            <ChatCircleDots size={16} className="text-purple-400" />
                          </button>
                        )}
                        <button onClick={() => handleEdit(e)} className="p-2 hover:bg-blue-100 rounded transition-colors" title="Izmijeni"><Pencil size={16} className="text-blue-600" /></button>
                        <button onClick={() => handleDelete(e.id)} className="p-2 hover:bg-red-100 rounded transition-colors" title="Obrisi"><Trash size={16} className="text-red-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <span className="text-xs text-slate-500">Prikazano: {filtered.length} od {totalEntries} zapisa (str. {page}/{totalPages || 1})</span>
            {totalPages > 1 && (
              <div className="flex gap-1 items-center">
                <button onClick={() => goPage(1)} disabled={page <= 1} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">«</button>
                <button onClick={() => goPage(page - 1)} disabled={page <= 1} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} onClick={() => goPage(p)}
                      className={`px-2.5 py-1 text-xs rounded border ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 hover:bg-slate-100'}`}>{p}</button>
                  );
                })}
                <button onClick={() => goPage(page + 1)} disabled={page >= totalPages} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">›</button>
                <button onClick={() => goPage(totalPages)} disabled={page >= totalPages} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">»</button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Notify Dialog */}
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="max-w-md" data-testid="notify-dialog">
          <DialogHeader><DialogTitle>{notifyChannel === 'viber' ? 'Pošalji Viber poruku' : 'Obavijesti klijenta emailom'}</DialogTitle></DialogHeader>
          {notifyEntry && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-slate-50 rounded border border-slate-200">
                <p className="text-xs text-slate-500">Klijent</p>
                <p className="text-sm font-medium">{getClientName(notifyEntry.client_id)}</p>
                {notifyChannel === 'viber' ? (
                  <>
                    <p className="text-xs text-slate-500 mt-2">Telefon</p>
                    <p className="text-sm">{clients.find(c => c.id === notifyEntry.client_id)?.telefon || '-'}</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mt-2">Email</p>
                    <p className="text-sm">{clients.find(c => c.id === notifyEntry.client_id)?.email || '-'}</p>
                  </>
                )}
                <p className="text-xs text-slate-500 mt-2">Posao</p>
                <p className="text-sm font-medium">{notifyEntry.opis_posla}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Dodatna poruka (opciono)</Label>
                <textarea
                  value={notifyMsg}
                  onChange={e => setNotifyMsg(e.target.value)}
                  placeholder="Npr: Molimo vas da preuzmete dokumenta u našoj kancelariji..."
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="notify-message-input"
                />
              </div>
              {notifyChannel === 'email' && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Priloži fajl (PDF, opciono)</Label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
                    onChange={e => setNotifyFile(e.target.files[0] || null)}
                    className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    data-testid="notify-file-input"
                  />
                  {notifyFile && <p className="text-xs text-blue-600 mt-1">{notifyFile.name} ({(notifyFile.size / 1024).toFixed(0)} KB)</p>}
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setNotifyOpen(false)}>Odustani</Button>
                {notifyChannel === 'viber' ? (
                  <Button onClick={handleNotifySend} disabled={notifySending} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="notify-send-viber-btn">
                    <ChatCircleDots size={16} className="mr-2" /> {notifySending ? 'Šaljem...' : 'Pošalji Viber'}
                  </Button>
                ) : (
                  <Button onClick={handleNotifySend} disabled={notifySending} className="bg-green-600 hover:bg-green-700 text-white" data-testid="notify-send-btn">
                    <EnvelopeSimple size={16} className="mr-2" /> {notifySending ? 'Šaljem...' : 'Pošalji email'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
